import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchDistricts } from '../api/districts-api';
import { searchSchools } from '../api/schools-api';
import type { DistrictSummary, SchoolSummary } from '../api/types';
import { ErrorBanner, LoadingBlock } from '../components/ui';

type ListingTab = 'all' | 'districts' | 'schools';
type DirectoryRow =
  | { type: 'District'; id: string; entity: DistrictSummary }
  | { type: 'School'; id: string; entity: SchoolSummary };

const PAGE_SIZE = 25;

export function DirectoryPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ListingTab>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [schools, setSchools] = useState<SchoolSummary[]>([]);
  const [districts, setDistricts] = useState<DistrictSummary[]>([]);
  const [totals, setTotals] = useState({ schools: 0, districts: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([searchSchools({ limit: 500 }), searchDistricts({ limit: 500 })])
      .then(([schoolResult, districtResult]) => {
        if (!active) return;
        setSchools(schoolResult.schools);
        setDistricts(districtResult.districts);
        setTotals({ schools: schoolResult.total, districts: districtResult.total });
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Unable to load schools and districts.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();
    const rows: DirectoryRow[] = [
      ...(tab === 'schools' ? [] : districts.map((entity) => ({ type: 'District' as const, id: entity.ndid, entity }))),
      ...(tab === 'districts' ? [] : schools.map((entity) => ({ type: 'School' as const, id: entity.nid, entity }))),
    ];
    if (!value) return rows;
    return rows.filter(({ type, entity }) => {
      const id = type === 'District' ? entity.ndid : entity.nid;
      const nces = type === 'District' ? entity.ncesDistrictId : entity.ncesId;
      return [entity.name, entity.city, entity.state, id, nces].some((field) =>
        String(field ?? '').toLowerCase().includes(value),
      );
    });
  }, [districts, schools, search, tab]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const counts = { all: totals.districts + totals.schools, districts: totals.districts, schools: totals.schools };
  const paginationPages = pageCount <= 7
    ? Array.from({ length: pageCount }, (_, index) => index + 1)
    : [1, 2, 3, 4, 5, 6, pageCount];

  const selectTab = (nextTab: ListingTab) => {
    setTab(nextTab);
    setPage(1);
  };

  return (
    <div className="directory-page">
      <header className="directory-header">
        <div>
          <h2>Search</h2>
          <p>Find a district or school by name, ID, or Salesforce ID.</p>
        </div>
        <div className="directory-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/highschools/new')}>+ Add school</button>
          <button type="button" className="button-primary" onClick={() => navigate('/districts/new')}>+ Add district</button>
        </div>
      </header>

      <section className="directory-panel" aria-labelledby="directory-listing-heading">
        <h3 id="directory-listing-heading" className="sr-only">District and school listing</h3>
        <div className="directory-search-row">
          <label className="directory-search">
            <span aria-hidden="true">⌕</span>
            <input
              aria-label="Search by name, ID, or Salesforce ID"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder="Search by name, district ID, or Salesforce ID"
            />
          </label>
          <button type="button" className="button-filter" onClick={() => setSearch('')}>Clear</button>
        </div>

        <div className="directory-tabs" role="tablist" aria-label="Listing type">
          {(['all', 'districts', 'schools'] as const).map((id) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'directory-tab active' : 'directory-tab'} onClick={() => selectTab(id)}>
              {id[0].toUpperCase() + id.slice(1)} <b>{counts[id].toLocaleString()}</b>
            </button>
          ))}
        </div>

        <ErrorBanner error={error} />
        {loading ? <LoadingBlock label="Loading schools and districts…" /> : (
          <>
            <div className="directory-table-wrap">
              <table className="directory-table">
                <thead><tr><th>Name</th><th>Type</th><th>State</th><th>Status</th><th>Naviance ID</th><th>Salesforce ID</th><th>Enrollment</th></tr></thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const entity = row.entity;
                    const nces = row.type === 'District'
                      ? row.entity.ncesDistrictId
                      : row.entity.ncesId;
                    return <tr key={`${row.type}-${row.id}`} onClick={() => navigate(row.type === 'District' ? `/districts/${row.id}` : `/highschools/${row.id}`)}>
                      <td><a href={row.type === 'District' ? `/districts/${row.id}` : `/highschools/${row.id}`} onClick={(event) => event.preventDefault()}>{entity.name}</a></td>
                      <td><span className="type-pill">♙ {row.type}</span></td><td>{entity.state || '—'}</td>
                      <td><span className="status-pill"><span className="status-dot">●</span> {entity.accountStatus}</span></td><td>{row.id}</td><td>{nces || '—'}</td><td>{entity.enrollment?.toLocaleString() || '—'}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
              {!visibleRows.length && <div className="empty">No records found.</div>}
            </div>
            <footer className="directory-footer">
              <span>{filteredRows.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of ${filteredRows.length.toLocaleString()} results` : '0 results'}</span>
              <div className="directory-pagination" aria-label="Pagination">
                <button type="button" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>‹</button>
                {paginationPages.map((paginationPage, index) => (
                  <span key={paginationPage} className="directory-page-control-group">
                    {index === 6 && pageCount > 7 ? <span className="directory-page-ellipsis">…</span> : null}
                    <button
                      type="button"
                      className={paginationPage === currentPage ? 'current' : ''}
                      aria-current={paginationPage === currentPage ? 'page' : undefined}
                      onClick={() => setPage(paginationPage)}
                    >
                      {paginationPage}
                    </button>
                  </span>
                ))}
                <button type="button" aria-label="Next page" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>›</button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}